import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { AuthService } from './auth.service';

/**
 * Attaches the backoffice token and treats 401 as "the session is over".
 *
 * There is no refresh flow on purpose. Backoffice tokens are short-lived (30m
 * by default) and the staff_users lookup runs on every request, so a revoked
 * account starts failing immediately — silently refreshing would paper over
 * exactly the signal we want to act on. Logging out and re-doing the Google
 * flow takes one click.
 *
 * Interceptors are inherently an RxJS API in Angular 22; there is no signal
 * equivalent. It applies to `httpResource` reads too, which is why the resources
 * in the components carry no auth code of their own.
 */
export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const auth = inject(AuthService);
  const token = auth.token();

  const authorized = token
    ? request.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : request;

  return next(authorized).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        auth.logout();
      }

      return throwError(() => error);
    })
  );
};
